/**
 * Utility functions for phone number processing
 */

/**
 * Clean and extract mobile number without country code
 * @param phoneNumber - The phone number to clean (can include country code)
 * @returns Clean 10-digit mobile number without country code
 */
export const cleanPhoneNumber = (phoneNumber: string | number): string => {
  if (!phoneNumber) return '';

  // Convert to string and trim
  let cleanPhone = phoneNumber.toString().trim();

  // Remove any non-digit characters first
  cleanPhone = cleanPhone.replace(/\D/g, '');

  // Remove country code prefixes (91 for India)
  if (cleanPhone.startsWith('91') && cleanPhone.length === 12) {
    // If it's 12 digits starting with 91, remove the first 2 digits (91)
    cleanPhone = cleanPhone.substring(2);
  } else if (cleanPhone.startsWith('91') && cleanPhone.length === 13) {
    // If it's 13 digits starting with 91, remove the first 3 digits (91x where x might be extra)
    cleanPhone = cleanPhone.substring(3);
  } else if (cleanPhone.startsWith('91') && cleanPhone.length > 10) {
    // For any other case starting with 91 and longer than 10 digits
    cleanPhone = cleanPhone.substring(2);
  }

  // Ensure we only take the last 10 digits if it's still longer
  if (cleanPhone.length > 10) {
    cleanPhone = cleanPhone.slice(-10);
  }

  return cleanPhone;
};

/**
 * Format mobile number with country code prefix for submission
 * @param phoneNumber - The phone number to format (can include country code)
 * @returns Mobile number with '91' prefix (12 digits total)
 */
export const formatPhoneNumberWithPrefix = (phoneNumber: string | number): string => {
  if (!phoneNumber) return '';

  // First clean the number to get 10 digits
  const cleaned = cleanPhoneNumber(phoneNumber);
  
  // If we have a valid 10-digit number, add '91' prefix
  if (cleaned.length === 10) {
    return `91${cleaned}`;
  }
  
  // If it already has the prefix and is 12 digits, return as is
  if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return cleaned;
  }
  
  // Return the cleaned number (fallback)
  return cleaned;
};

/**
 * Get common phone field configuration for forms
 * @param required - Whether the field is required
 * @param placeholder - Custom placeholder text
 * @returns Common phone field configuration
 */
export const getPhoneFieldConfig = (required = false, placeholder = '98765 43210', className = '') => ({
  type: 'telephone' as const,
  required,
  placeholder,
  props: {
    size: 'large',
    defaultCountry: 'IN',
    international: false,
    withCountryCallingCode: false,
    maxLength: 10,
    className: className,
  },
  validation: {
    validator: (_: any, value: string) => {
      if (!value) {
        return Promise.resolve();
      }
      
      // Clean the value to remove any non-digits and country code
      const cleanValue = value.replace(/\D/g, '');
      
      // Remove country code if present
      const phoneNumber = cleanValue.startsWith('91') && cleanValue.length === 12 
        ? cleanValue.substring(2) 
        : cleanValue;
      
      // Check if it's exactly 10 digits and starts with 2-9
      if (phoneNumber.length !== 10) {
        return Promise.reject(new Error('Please enter a valid 10-digit mobile number'));
      }
      
      if (!/^[2-9]/.test(phoneNumber)) {
        return Promise.reject(new Error('Please enter a valid 10-digit mobile number starting with 2-9'));
      }
      
      return Promise.resolve();
    },
  },
  tooltip: 'Enter 10-digit mobile number (without country code)',
  transform: {
    input: (value: any) => {
      // Clean the input value - use cleanPhoneNumber for consistent handling
      if (value === null || value === undefined || value === '') {
        return value;
      }
      return cleanPhoneNumber(value);
    },
    output: (value: any) => {
      // Format the output value with '91' prefix for backend submission
      if (value === null || value === undefined || value === '') {
        return value;
      }
      return formatPhoneNumberWithPrefix(value);
    },
  },
});
